import { Link } from "react-router";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import logo from "#app/images/main-logo-color.webp";
import { cn } from "#app/lib/utils.ts";

const layoutVariants = cva(
  "mx-2 my-8 flex flex-1 flex-col justify-center sm:mx-auto sm:w-full",
  {
    variants: {
      variant: {
        narrow: "sm:max-w-md",
        normal: "sm:max-w-lg",
        wide: "sm:max-w-4xl",
      },
    },
    defaultVariants: {
      variant: "normal",
    },
  },
);

export type MinimalLayoutProps = VariantProps<typeof layoutVariants> & {
  title: string;
  message?: ReactNode;

  children: ReactNode;
};

export function MinimalLayout({
  title,
  message,
  variant,
  children,
}: MinimalLayoutProps) {
  const wideClass = cn(layoutVariants({ variant }));

  return (
    <div className="bg-foreground relative flex min-h-full flex-col px-2 sm:px-6 lg:px-8">
      <div className={wideClass}>
        <div className="bg-card text-card-foreground rounded-md border px-4 py-8 shadow-sm sm:rounded-lg sm:px-10">
          <div className="space-y-4">
            <Link to="/">
              <img
                className="mx-auto h-40 w-auto sm:h-48"
                src={logo}
                alt="PHA Logo"
              />
            </Link>
            <h1 className="text-primary mt-3 text-center text-3xl font-extrabold">
              {title}
            </h1>
            {message && (
              <div className="text-card-foreground rounded-md p-3 text-sm font-semibold whitespace-pre-line">
                {message}
              </div>
            )}

            {children}
          </div>
        </div>
      </div>

      <footer className="text-background flex flex-col items-center justify-center space-y-1 py-4 text-center sm:flex-row sm:space-y-0">
        <span>
          &copy; {new Date().getFullYear()} Palmetto Homeschool Association
        </span>

        <hr className="bg-background/50 h-[0.5px] w-48 border-0 sm:hidden" />
        <span className="mx-2 hidden sm:block">&bull;</span>

        <Link
          to="/privacy-policy"
          className="hover:underline hover:underline-offset-2"
        >
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
